<script module lang="ts">
	export enum ToggleSize {
		SMALL,
		MEDIUM
	}
</script>

<script lang="ts">
	import { Icon } from '@repo/commons/components'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface Props {
		value?: boolean
		size?: ToggleSize
		label?: string
		tooltip?: TooltipProps
		onchange?: ((value: boolean) => void) | null
	}

	let {
		value = $bindable(false),
		size = ToggleSize.SMALL,
		label = 'Label',
		tooltip,
		onchange = null
	}: Props = $props()

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	function toggle() {
		value = !value
		onchange?.(value)
	}
</script>

<div
	class="toggle-container {size === ToggleSize.MEDIUM ? 'toggle-medium' : 'toggle-small'}"
	onclick={toggle}
>
	<div class="toggle-track {size === ToggleSize.MEDIUM ? 'track-medium' : 'track-small'}">
		<div
			class={size === ToggleSize.MEDIUM
				? value
					? 'track-active-medium'
					: 'track-inactive-medium'
				: value
					? 'track-active-small'
					: 'track-inactive-small'}
		>
			<div class={size === ToggleSize.MEDIUM ? 'track-inner-medium' : 'track-inner-small'}></div>
		</div>
		<div
			class={size === ToggleSize.MEDIUM
				? value
					? 'thumb-active-medium'
					: 'thumb-inactive-medium'
				: value
					? 'thumb-active-small'
					: 'thumb-inactive-small'}
		></div>
	</div>
	<div
		class="label-wrapper"
		class:label-medium={size === ToggleSize.MEDIUM}
		class:label-small={size === ToggleSize.SMALL}
	>
		{label}
		{#if tooltip?.text || tooltip?.html}
			{#await import('@repo/commons/components') then { Tooltip }}
				<Tooltip {...tooltipProps}>
					<Icon name="info" size="sm" class="text-brand" />
				</Tooltip>
			{/await}
		{/if}
	</div>
</div>

<style>
	.toggle-container {
		@apply justify-start items-center gap-3 inline-flex;
	}
	.toggle-medium {
		@apply h-8;
	}
	.toggle-small {
		@apply h-6;
	}
	.toggle-track {
		@apply relative rounded-[32px];
	}
	.track-medium {
		@apply w-16 h-8;
	}
	.track-small {
		@apply w-12 h-6;
	}
	.track-inactive-medium {
		@apply w-16 h-8 absolute left-0 top-0 bg-[#004980]/5 rounded-[32px] border border-[#002c4d]/50 justify-center items-center inline-flex;
	}
	.track-active-medium {
		@apply w-16 h-8 absolute left-0 top-0 bg-[#007acc] rounded-[32px] justify-center items-center inline-flex;
	}
	.track-inactive-small {
		@apply w-12 h-6 absolute left-0 top-0 bg-[#004980]/5 rounded-[32px] border border-[#002c4d]/50 justify-center items-center inline-flex;
	}
	.track-active-small {
		@apply w-12 h-6 absolute left-0 top-0 bg-[#007acc] rounded-[32px] justify-center items-center inline-flex;
	}
	.track-inner-medium {
		@apply w-16 h-8 relative rounded-[32px];
	}
	.track-inner-small {
		@apply w-12 h-6 relative rounded-[32px];
	}
	.thumb-inactive-medium {
		@apply w-8 h-8 absolute left-0 top-0 bg-white rounded-full shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.04)] shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.08)] border-2 border-[#002c4d]/50;
		transition: left 0.3s ease-in-out;
	}
	.thumb-active-medium {
		@apply w-8 h-8 absolute left-[32px] top-0 bg-white rounded-full shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.04)] shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.08)] border-2 border-[#007acc];
		transition: left 0.3s ease-in-out;
	}
	.thumb-inactive-small {
		@apply w-6 h-6 absolute left-0 top-0 bg-white rounded-full shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.04)] shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.08)] border-2 border-[#002c4d]/50;
		transition: left 0.3s ease-in-out;
	}
	.thumb-active-small {
		@apply w-6 h-6 absolute left-[24px] top-0 bg-white rounded-full shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.04)] shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.08)] border-2 border-[#007acc];
		transition: left 0.3s ease-in-out;
	}
	.label-wrapper {
		@apply inline-flex items-center gap-1;
	}
	.label-medium {
		@apply text-[#001526]/90 text-base font-normal leading-normal;
	}
	.label-small {
		@apply text-[#001526]/90 text-sm font-normal leading-tight;
	}
	/* Animate background color transition on the track */
	.track-inactive-medium,
	.track-active-medium,
	.track-inactive-small,
	.track-active-small {
		transition: background-color 0.3s ease-in-out;
	}
</style>
